import { useState, useEffect } from 'react';
import { usePlatform } from './usePlatform';

// Breakpoints following mobile-first approach
const BREAKPOINTS = {
  xs: 0,     // Extra small devices (phones)
  sm: 576,   // Small devices (landscape phones)
  md: 768,   // Medium devices (tablets)
  lg: 992,   // Large devices (desktops)
  xl: 1200,  // Extra large devices (large desktops)
  xxl: 1400  // Extra extra large devices
};

// Media query helper (for future use)
// const createMediaQuery = (minWidth) => `(min-width: ${minWidth}px)`;

export const useResponsive = () => {
  const platform = usePlatform();
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  });

  const [orientation, setOrientation] = useState(
    typeof window !== 'undefined' && window.innerWidth > window.innerHeight 
      ? 'landscape' 
      : 'portrait'
  );

  useEffect(() => {
    const handleResize = () => {
      const newWidth = window.innerWidth;
      const newHeight = window.innerHeight;
      
      setWindowSize({
        width: newWidth,
        height: newHeight,
      });
      
      setOrientation(newWidth > newHeight ? 'landscape' : 'portrait');
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const width = windowSize.width;
  const height = windowSize.height;

  return {
    // Window dimensions
    width,
    height,
    orientation,
    
    // Device type detection (enhanced with platform info)
    isMobile: platform.isMobile || width < BREAKPOINTS.md,
    isTablet: platform.isTablet || (width >= BREAKPOINTS.md && width < BREAKPOINTS.lg),
    isDesktop: platform.isDesktop || width >= BREAKPOINTS.lg,
    
    // Platform information
    ...platform,
    
    // Specific breakpoint checks
    isXs: width < BREAKPOINTS.sm,
    isSm: width >= BREAKPOINTS.sm && width < BREAKPOINTS.md,
    isMd: width >= BREAKPOINTS.md && width < BREAKPOINTS.lg,
    isLg: width >= BREAKPOINTS.lg && width < BREAKPOINTS.xl,
    isXl: width >= BREAKPOINTS.xl && width < BREAKPOINTS.xxl,
    isXxl: width >= BREAKPOINTS.xxl,
    
    // Minimum breakpoint checks (mobile-first)
    isSmUp: width >= BREAKPOINTS.sm,
    isMdUp: width >= BREAKPOINTS.md,
    isLgUp: width >= BREAKPOINTS.lg,
    isXlUp: width >= BREAKPOINTS.xl,
    isXxlUp: width >= BREAKPOINTS.xxl,
    
    // Maximum breakpoint checks
    isSmDown: width < BREAKPOINTS.md,
    isMdDown: width < BREAKPOINTS.lg,
    isLgDown: width < BREAKPOINTS.xl,
    isXlDown: width < BREAKPOINTS.xxl,
    
    // Touch device detection
    isTouchDevice: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
    
    // PWA detection (enhanced)
    isPWA: platform.isStandalone || 
           window.matchMedia('(display-mode: standalone)').matches ||
           window.navigator.standalone === true,
    
    // Breakpoint values
    breakpoints: BREAKPOINTS,
    
    // Utility functions
    getBreakpoint: () => {
      if (width < BREAKPOINTS.sm) return 'xs';
      if (width < BREAKPOINTS.md) return 'sm';
      if (width < BREAKPOINTS.lg) return 'md';
      if (width < BREAKPOINTS.xl) return 'lg';
      if (width < BREAKPOINTS.xxl) return 'xl';
      return 'xxl';
    },
    
    getColumns: () => {
      if (width < BREAKPOINTS.sm) return 1;
      if (width < BREAKPOINTS.md) return 2;
      if (width < BREAKPOINTS.lg) return 3;
      if (width < BREAKPOINTS.xl) return 4;
      return 5;
    },
    
    // Platform-specific utility functions
    getPlatformClasses: platform.getPlatformClasses,
    getPlatformStyles: platform.getPlatformStyles,
    getSafeAreaPadding: platform.getSafeAreaPadding,
  };
};

// Hook for media queries
export const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const media = window.matchMedia(query);
    if (media.matches !== matches) {
      setMatches(media.matches);
    }
    const listener = () => setMatches(media.matches);
    media.addEventListener('change', listener);
    return () => media.removeEventListener('change', listener);
  }, [matches, query]);

  return matches;
};

// Predefined media query hooks
export const useMobileFirst = () => {
  const isMobile = useMediaQuery(`(max-width: ${BREAKPOINTS.md - 1}px)`);
  const isTablet = useMediaQuery(`(min-width: ${BREAKPOINTS.md}px) and (max-width: ${BREAKPOINTS.lg - 1}px)`);
  const isDesktop = useMediaQuery(`(min-width: ${BREAKPOINTS.lg}px)`);
  
  return { isMobile, isTablet, isDesktop };
};

// Hook for PWA install prompt
export const usePWAInstall = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
    };

    // Check if app is already installed
    if (window.matchMedia('(display-mode: standalone)').matches || 
        window.navigator.standalone === true) {
      setIsInstalled(true);
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const showInstallPrompt = async () => {
    if (!deferredPrompt) return false;
    
    try {
      await deferredPrompt.prompt();
      const choiceResult = await deferredPrompt.userChoice;
      
      if (choiceResult.outcome === 'accepted') {
        setIsInstallable(false);
        setDeferredPrompt(null);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error showing install prompt:', error);
      return false;
    }
  };

  return {
    isInstallable,
    isInstalled,
    showInstallPrompt,
  };
};

export default useResponsive; 